/**
 * The Attribution page's derived figures, in one place: the unattributed row and its
 * share, and the ratios the API leaves to the caller (attribution.md §6 — "Ratios are
 * not served"). Every derivation here returns null rather than a number it cannot
 * stand behind, and the page renders null as "—".
 */

import {
  META_GROUP_BYS,
  type AttributionGroupBy,
  type AttributionMetrics,
  type AttributionReport,
  type AttributionRow,
} from '@/api/attribution'
import { ATTRIBUTION_MODELS, type AttributionModel } from '@/api/types'
import { isNotTracked, NOT_TRACKED_EXPLANATION, NOT_TRACKED_LABEL, notTrackedLast, splitsNotTracked } from '@/components/dataQuality'
import { isNonProduction } from '@/lib/environment'
import { DIMENSION_LABELS, DIMENSIONS, type GlobalFilters } from '@/lib/filters'

export const ZERO_METRICS: AttributionMetrics = { purchases: 0, revenueMinor: 0, newCustomers: 0, refunds: 0, refundMinor: 0 }

/**
 * What the all-null row holds at each grouping, named honestly. A touch's source,
 * medium and campaign are its UTM tags only (`touches._fields`), so the row holds more
 * than orders with no touch: direct visits, and untagged search, referral and ad clicks.
 */
export const UNATTRIBUTED_LABELS: Record<AttributionGroupBy, string> = {
  campaign: 'No campaign tag (unattributed, direct or untagged)',
  source_medium: 'No UTM source (unattributed, direct or untagged)',
  ad_campaign: 'Not from a Meta ad (incl. unattributed)',
  ad_set: 'Not from a Meta ad (incl. unattributed)',
  ad: 'Not from a Meta ad (incl. unattributed)',
}

/**
 * The null-id row's name once the API reports Not tracked separately (the coverage card,
 * wave 20 B1): it then holds credited touches only — direct visits, and untagged search,
 * referral and ad clicks — and the orders with no tracked touch are the Not tracked row.
 */
export const NO_TAG_LABELS: Record<AttributionGroupBy, string> = {
  campaign: 'No campaign tag (direct or untagged)',
  source_medium: 'No UTM source (direct or untagged)',
  ad_campaign: 'Not from a Meta ad',
  ad_set: 'Not from a Meta ad',
  ad: 'Not from a Meta ad',
}

/** Whether the report splits the Not tracked row out of the null-id row (`@/components/dataQuality`). */
export { splitsNotTracked }

/** What a row is, for its name cell and its place in the table. */
export type RowKind = 'group' | 'unattributed' | 'notTracked'

/**
 * A named group or the null-id touch row (`group`); on an older API's report the null-id
 * row that also held the orders with no touch (`unattributed`); or the Not tracked row.
 */
export function rowKind(row: AttributionRow, split: boolean): RowKind {
  if (isNotTracked(row)) return 'notTracked'
  return row.id === null && !split ? 'unattributed' : 'group'
}

/** What the Not tracked share counts, beside it in the totals. */
export const NOT_TRACKED_SHARE_DETAIL = 'Orders with no tracked visit (never merged with direct)'

/** The Not tracked row's explanation: what it holds, and the lookback it was judged over. */
export function notTrackedExplanation(lookbackDays: number): string {
  return `${NOT_TRACKED_EXPLANATION} Judged over the ${lookbackDays} days before each purchase. Kept, never dropped, so every model's totals still add up to every order.`
}

/** attribution.md §4, in the page's words: what "unattributed" means, then what else shares the row at this grouping. */
export function unattributedExplanation(groupBy: AttributionGroupBy, lookbackDays: number): string {
  const base = `Unattributed sales are orders with no visit or campaign touch in the ${lookbackDays} days before the purchase. They are kept, never dropped, so every model's totals still add up to every order. Sources here are read from UTM tags only, so a visit without them — direct, or an untagged search, referral or ad click — has no source, medium or campaign either, and its sales share this row. The By channel table below sorts those visits into channels and counts only sales with no touch at all as unattributed.`
  if (groupBy === 'campaign') return `${base} Grouped by campaign, it also holds sales credited to a tagged visit that carried no campaign.`
  if (META_GROUP_BYS.has(groupBy)) return `${base} Grouped by a Meta level, it also holds every sale not credited to a Meta ad.`
  return base
}

/** The null-id touch row (on an older API, the all-null row that also held the orders with no touch); never the Not tracked row. */
export function isNullIdRow(row: AttributionRow): boolean {
  return row.id === null && !isNotTracked(row)
}

/** A table key no real group can collide with, the null-id and Not tracked rows included. */
export function rowKey(row: { id: string | null; credit?: AttributionRow['credit'] }): string {
  if (row.credit === 'unattributed') return 'not-tracked'
  return row.id === null ? 'unattributed' : `id:${row.id}`
}

/** A row's display name: the Meta object's name, else its id; the null-id and Not tracked rows by what they hold. */
export function rowLabel(row: AttributionRow, groupBy: AttributionGroupBy, split = false): string {
  if (isNotTracked(row)) return NOT_TRACKED_LABEL
  if (row.id === null) return (split ? NO_TAG_LABELS : UNATTRIBUTED_LABELS)[groupBy]
  return row.name ?? row.id
}

/** The traffic API's own name for its null-key channel row (`tally.UNATTRIBUTED_LABEL`): strictly no touch in the lookback. */
export const NO_TOUCH_LABEL = 'Unattributed (no touch in the lookback)'

/**
 * `rows` with exactly one unattributed row, last: the one the API sent, wherever it
 * was, or `zero()` when it sent none — whose absence means nothing was unattributed,
 * so the row is shown as zero rather than hidden. Both tables on the page use this rule.
 */
export function withTrailingUnattributed<T>(rows: readonly T[], unattributed: (row: T) => boolean, zero: () => T): T[] {
  return [...rows.filter((row) => !unattributed(row)), rows.find(unattributed) ?? zero()]
}

/**
 * The report's rows as the table shows them. From an older API: exactly one unattributed
 * row, last — the API sends it last whenever any order credits the all-null row; when none
 * does it sends no such row, and that absence *is* zero, so the page shows a zero row
 * rather than hide it. Since the coverage card: the groups, the null-id touch row and the
 * Not tracked row last, each only as the API sent it — never one made up.
 */
export function rowsWithUnattributed(report: AttributionReport): AttributionRow[] {
  if (splitsNotTracked(report)) {
    const nullIdRowLast = notTrackedLast(report.rows, isNullIdRow)
    return notTrackedLast(nullIdRowLast)
  }
  return withTrailingUnattributed(report.rows, isNullIdRow, () => ({
    id: null,
    name: null,
    key: {},
    model: report.model,
    tmtAttributed: true as const,
    metrics: ZERO_METRICS,
    previous: report.compared ? ZERO_METRICS : null,
    spendMinor: report.spendSource === 'meta' ? 0 : null,
    previousSpendMinor: report.spendSource === 'meta' && report.compared ? 0 : null,
  }))
}

/**
 * The unattributed figures the totals state, under both APIs. Since the coverage card
 * they are the Not tracked row's — the API's `credit: 'unattributed'`, hence the name —
 * null when the API sent none, so nothing is stated. From an older API they are the
 * all-null row's, which also held direct and untagged touches; zero when it sent none.
 */
export function unattributedMetrics(report: AttributionReport): AttributionMetrics | null {
  if (splitsNotTracked(report)) return report.rows.find(isNotTracked)?.metrics ?? null
  return report.rows.find(isNullIdRow)?.metrics ?? ZERO_METRICS
}

/** The heading of the stated unattributed share: Not tracked once the report splits it out (`unattributedMetrics`). */
export function shareHeading(split: boolean): string {
  return split ? 'Not tracked share' : 'Unattributed share'
}

/** `part / whole` as a fraction; null when the whole is zero (no sales, so no share to state). */
export function shareOf(part: number, whole: number): number | null {
  return whole === 0 ? null : part / whole
}

export interface UnattributedShare {
  revenue: number | null
  purchases: number | null
}

/** `unattributedMetrics` as shares of the totals; null when there are none to state. */
export function unattributedShare(report: AttributionReport): UnattributedShare | null {
  const unattributed = unattributedMetrics(report)
  if (!unattributed) return null
  return {
    revenue: shareOf(unattributed.revenueMinor, report.totals.revenueMinor),
    purchases: shareOf(unattributed.purchases, report.totals.purchases),
  }
}

/**
 * Whether the report carries spend a ratio may be built on: a Meta level, and Meta
 * reported some spend in the range. At a Meta level the API sends `0` for an object
 * with no spend, which is indistinguishable from "Meta is not connected" when the whole
 * range is zero — so a zero total is treated as no spend, never as a free campaign.
 */
export function hasSpend(report: AttributionReport): boolean {
  return report.spendSource === 'meta' && report.totalSpendMinor !== null && report.totalSpendMinor > 0
}

/**
 * A row's spend as a ratio may use it: null unless the report has spend at all, and
 * always null for the null-id and Not tracked rows — neither is a Meta object, so the
 * API's `0` for them is not a spend Meta reported.
 */
export function rowSpendMinor(report: AttributionReport, row: AttributionRow): number | null {
  return hasSpend(report) && row.id !== null ? row.spendMinor : null
}

/** Cost per acquisition, minor units: spend ÷ purchases. Null without spend (zero included) or purchases. */
export function cpaMinor(spendMinor: number | null, purchases: number): number | null {
  if (spendMinor === null || spendMinor <= 0 || purchases === 0) return null
  return Math.round(spendMinor / purchases)
}

/** Return on ad spend: revenue ÷ spend. Null without spend (zero spend has no return ratio). */
export function roas(revenueMinor: number, spendMinor: number | null): number | null {
  if (spendMinor === null || spendMinor <= 0) return null
  return revenueMinor / spendMinor
}

export const PROVIDER_SPEND_LABEL = 'Provider-reported (Meta)'
export const SPEND_NOT_LIVE_LABEL = 'Not live yet — needs ad platform'

/**
 * The source Spend, CPA and ROAS carry: Meta's at a Meta level — even when Meta reported
 * none, which the cells show as "—" and the tooltip explains — and plainly not live
 * where the grouping carries no spend at all.
 */
export function spendSourceLabel(report: AttributionReport): string {
  return report.spendSource === 'meta' ? PROVIDER_SPEND_LABEL : SPEND_NOT_LIVE_LABEL
}

export const META_SPEND_CAVEAT =
  "Spend as Meta reports it for the same ads and days, converted into your reporting currency. It is the ad platform's figure, not counted by Competition Insights; CPA and ROAS divide it by the TmT-attributed purchases and revenue beside it."

/** Why Spend, CPA and ROAS show "—": the grouping carries no spend, or Meta reported none for the range. */
export function spendUnavailableReason(report: AttributionReport): string {
  if (report.spendSource !== 'meta') {
    return 'Ad spend is joined per Meta campaign, ad set and ad only, so it is not shown by campaign tag or source / medium. Group by a Meta level once an ad platform is connected to see spend, CPA and ROAS.'
  }
  return 'No Meta spend was reported for this period, so CPA and ROAS cannot be worked out. They appear once Meta is connected and an ad account is mapped to a website.'
}

/**
 * Nothing to attribute: no purchase or refund in the range or its comparison period,
 * and no spend either. The one "no sales" rule both views use.
 */
export function isEmptyReport(report: AttributionReport): boolean {
  const quiet = (metrics: AttributionMetrics | null) => !metrics || (metrics.purchases === 0 && metrics.refunds === 0)
  const spent = report.rows.some((row) => (row.spendMinor ?? 0) > 0 || (row.previousSpendMinor ?? 0) > 0)
  return quiet(report.totals) && quiet(report.totalsPrevious) && !spent
}

/** Whether a Meta grouping found any Meta object at all — none means Meta data is not flowing for this range. */
export function hasMetaObjects(report: AttributionReport): boolean {
  return report.rows.some((row) => row.id !== null)
}

export interface ComparisonCell {
  revenueMinor: number
  purchases: number
}

/** One group across the four models: its figures under each, and how far apart the models put its revenue. */
export interface ComparisonRow {
  id: string | null
  credit?: AttributionRow['credit']
  kind: RowKind
  label: string
  byModel: Record<AttributionModel, ComparisonCell>
  /** The largest model's revenue less the smallest's: how much of this group's credit depends on the model. */
  spreadMinor: number
}

/**
 * Every group any model credits, with each model's figures (zero where a model credits
 * it nothing), largest revenue first by whichever model credits it most, the
 * null-id row after them (always present from an older API) and the Not tracked row last.
 */
export function comparisonRows(
  reports: ReadonlyArray<{ model: AttributionModel; report: AttributionReport }>,
  groupBy: AttributionGroupBy,
): ComparisonRow[] {
  const zero = (): Record<AttributionModel, ComparisonCell> =>
    Object.fromEntries(ATTRIBUTION_MODELS.map((model) => [model, { revenueMinor: 0, purchases: 0 }])) as Record<
      AttributionModel,
      ComparisonCell
    >
  const byKey = new Map<string, ComparisonRow>()
  for (const { model, report } of reports) {
    const split = splitsNotTracked(report)
    for (const row of rowsWithUnattributed(report)) {
      const key = rowKey(row)
      const entry = byKey.get(key) ?? {
        id: row.id,
        ...(row.credit ? { credit: row.credit } : {}),
        kind: rowKind(row, split),
        label: rowLabel(row, groupBy, split),
        byModel: zero(),
        spreadMinor: 0,
      }
      entry.byModel[model] = { revenueMinor: row.metrics.revenueMinor, purchases: row.metrics.purchases }
      byKey.set(key, entry)
    }
  }
  const rows = [...byKey.values()].map((row) => {
    const revenues = ATTRIBUTION_MODELS.map((model) => row.byModel[model].revenueMinor)
    return { ...row, spreadMinor: Math.max(...revenues) - Math.min(...revenues) }
  })
  const peak = (row: ComparisonRow) => Math.max(...ATTRIBUTION_MODELS.map((model) => row.byModel[model].revenueMinor))
  const sorted = rows.sort((a, b) => Number(a.id === null) - Number(b.id === null) || peak(b) - peak(a) || (a.id ?? '').localeCompare(b.id ?? ''))
  return notTrackedLast(sorted, (row) => row.kind === 'notTracked')
}

/**
 * Whether the four models credit the same sales in total. The API puts every order in
 * exactly one row under each model (attribution.md §4: "every model's totals reconcile
 * with the day's orders"), so they should; the page only says so when they do.
 */
export function totalsAgree(reports: ReadonlyArray<{ report: AttributionReport }>): boolean {
  const [first, ...rest] = reports
  if (!first) return false
  return rest.every(
    ({ report }) =>
      report.totals.purchases === first.report.totals.purchases &&
      report.totals.revenueMinor === first.report.totals.revenueMinor,
  )
}

/**
 * The filter bar's choices this page cannot apply, in words: the route is production
 * only across every website, takes none of the bar's dimensions, and compares with the
 * previous period only. Empty when everything on the bar applies.
 */
export function unappliedFilters(filters: GlobalFilters): string[] {
  const unapplied: string[] = []
  if (isNonProduction(filters.environments)) unapplied.push('environment')
  for (const dimension of DIMENSIONS) {
    if (filters.dimensions[dimension]) unapplied.push(DIMENSION_LABELS[dimension].toLowerCase())
  }
  if (filters.compare === 'previous_year') unapplied.push('year-on-year comparison')
  return unapplied
}
